import { clearProfileCache, loadProfileCache, saveProfileCache } from '@/lib/offline/profile-cache'
import { hasCriticalOpsInFlight } from '@/lib/offline/critical-ops'
import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase, refreshSessionSafe, DEV_BYPASS_AUTH, SUPABASE_STORAGE_KEY } from '@/lib/supabase/client'
import { useAuthStore } from '@/stores'
import type { User, Organization } from '@/types'

// True when the user is mid-edit (open dialog/alertdialog or focus in a text
// field / contenteditable) OR a critical op is in flight (photo upload etc).
// Used to defer the self-heal page reload so it can't wipe an in-progress
// form OR kill a photo upload mid-request — reloading destroys the File
// object and the photo is lost forever, since File only lives in memory.
function shouldDeferReload(): boolean {
  try {
    if (hasCriticalOpsInFlight()) return true
    if (
      document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      )
    ) {
      return true
    }
    const el = document.activeElement as HTMLElement | null
    if (el) {
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (el.isContentEditable) return true
    }
  } catch {
    /* defensive: never let this check throw into the refresh path */
  }
  return false
}

// Mock data for dev mode — use real org ID so Supabase queries work with RLS
const DEV_ORG_ID = '896d5c9e-888f-4a81-97c3-02b42ece8eda'
const DEV_USER_ID = 'bdc65d98-e1bd-4a60-b6de-b9a36ebe20aa'

const DEV_USER = {
  id: DEV_USER_ID,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  email: 'Mitchelltucker5@gmail.com',
  full_name: 'Mitchell Tucker',
  avatar_url: null,
  organization_id: DEV_ORG_ID,
  role: 'admin',
  portal_type: 'internal',
  page_permissions: ['*'],
  status: 'active',
} as unknown as User

const DEV_ORGANIZATION = {
  id: DEV_ORG_ID,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  name: 'Cornerstone Construction Group',
  slug: 'cornerstone',
  logo_url: null,
  settings: {},
} as unknown as Organization

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const {
    setSession,
    setSupabaseUser,
    setUser,
    setOrganization,
    setIsLoading,
    setIsInitialized,
    reset,
  } = useAuthStore()

  useEffect(() => {
    let mounted = true

    // DEV MODE: Bypass auth entirely
    if (DEV_BYPASS_AUTH) {
      setUser(DEV_USER)
      setOrganization(DEV_ORGANIZATION)
      setSession({ user: { id: DEV_USER.id, email: DEV_USER.email } } as never)
      setIsLoading(false)
      setIsInitialized(true)
      console.log('🔧 DEV MODE: Auth bypassed with mock user')
      return
    }

    const initAuth = async () => {
      try {
        // Wrap getSession() with a timeout to prevent indefinite hang
        // caused by stuck navigator.locks in Supabase JS v2.
        // Retry with backoff on transient failures (Supabase/Cloudflare 522s,
        // network blips) — do NOT clear storage on transient errors. Only a
        // real invalid-token error should wipe the session, and Supabase
        // surfaces those via `error`, not via a thrown timeout.
        const getSessionWithTimeout = (timeoutMs: number) =>
          Promise.race([
            supabase.auth.getSession(),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error(`getSession() timed out after ${timeoutMs}ms`)), timeoutMs)
            ),
          ])

        // Detect "real" auth failures (token invalid/expired/refresh failed).
        // These justify clearing storage. Network/timeout/5xx do NOT.
        const isInvalidTokenError = (err: unknown): boolean => {
          const msg = err instanceof Error ? err.message : String(err ?? '')
          return /invalid.*(jwt|token|refresh)|refresh.*token.*not.*found|token.*expired|user.*from.*sub.*claim/i.test(msg)
        }

        let session = null
        let getSessionError: unknown = null
        const attempts = [
          { timeout: 5000, backoff: 0 },
          { timeout: 8000, backoff: 1500 },
          { timeout: 10000, backoff: 3000 },
        ]

        for (let i = 0; i < attempts.length; i++) {
          const { timeout, backoff } = attempts[i]
          if (backoff > 0) await new Promise((r) => setTimeout(r, backoff))
          try {
            const { data, error } = await getSessionWithTimeout(timeout)
            if (error) {
              getSessionError = error
              if (isInvalidTokenError(error)) {
                // Real invalid-token — stop retrying, clear storage below
                break
              }
              // Transient supabase-side error — retry
              console.warn(`Auth getSession returned error (attempt ${i + 1}/${attempts.length}):`, error.message)
              continue
            }
            session = data.session
            getSessionError = null
            break
          } catch (err) {
            getSessionError = err
            console.warn(`Auth getSession attempt ${i + 1}/${attempts.length} failed:`, err)
            // Network timeouts are transient — keep retrying, don't touch storage
          }
        }

        // Only clear storage on genuine invalid-token errors. Transient network
        // failures leave storage alone so the next page load / focus event can
        // recover the session without forcing the user back to the login screen.
        if (getSessionError && isInvalidTokenError(getSessionError)) {
          try {
            localStorage.removeItem(SUPABASE_STORAGE_KEY)
            console.log('Cleared invalid auth token from storage')
          } catch (_) { /* ignore storage errors */ }
        } else if (getSessionError) {
          console.warn('Auth init exhausted retries, continuing without session (storage preserved for recovery)')
        }

        if (!mounted) return

        setSession(session)
        setSupabaseUser(session?.user ?? null)

        if (session?.user) {
          await fetchUserData(session.user.id)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        if (mounted) {
          setIsLoading(false)
          setIsInitialized(true)
        }
      }
    }

    // Drop a session whose profile row doesn't exist. signOut() can 403 on a
    // deleted auth user, so clear storage first and treat the call as advisory.
    const clearZombieSession = async () => {
      // /signup and /invite/:token run their own sign-in during submission —
      // never yank the session out from under them mid-flow.
      const path = window.location.pathname
      if (path.startsWith('/signup') || path.startsWith('/invite')) return
      try { localStorage.removeItem(SUPABASE_STORAGE_KEY) } catch { /* ignore */ }
      clearProfileCache()
      await supabase.auth.signOut().catch(() => {})
      if (!mounted) return
      setSession(null)
      setSupabaseUser(null)
      setUser(null)
      setOrganization(null)
    }

    const fetchUserData = async (userId: string, retries = 3) => {
      try {
        // Fetch user profile with organization
        // NOTE: Use explicit columns instead of '*' — the Supabase API gateway
        // strips '*' from select params, producing a leading comma → 406 error
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            id, created_at, updated_at, email, full_name, avatar_url,
            organization_id, role, department, title, phone,
            primary_location_id, certifications, safety_training_date,
            emergency_contact, vendor_id, client_id,
            portal_type, page_permissions, column_permissions, status,
            is_platform_admin, is_super_admin, must_change_password, can_manage_parts,
            can_view_financials, can_review_receipts, capabilities,
            organizations (
              id, created_at, updated_at, name, slug, logo_url, settings, plan_id,
              icon_url, report_logo_url, report_icon_url,
              primary_color, secondary_color, accent_color,
              report_footer_text, company_display_name, billing_tier
            )
          `)
          .eq('id', userId)
          .single()

        if (userError) {
          // Retry on transient errors (503/504/timeout/abort) — Supabase API gateway
          // can temporarily go down under heavy Edge Function load, and the client's
          // 15s fetch timeout can abort the initial profile fetch on a slow network.
          const msg = userError.message || ''
          const isTransient = msg.includes('upstream') || msg.includes('Failed to fetch')
            || msg.includes('503') || msg.includes('504') || msg.includes('timeout')
            || msg.includes('abort') || msg.includes('Abort')
          // With no service, don't burn 12s of retries — fall straight back to
          // the last profile this device saw so the installed app still opens.
          if (isTransient && (retries === 0 || navigator.onLine === false)) {
            const cached = loadProfileCache(userId)
            if (cached && mounted) {
              console.warn(`User profile fetch failed (${msg}); using cached profile`)
              setUser(cached.user)
              setOrganization(cached.organization)
              return
            }
          }
          if (isTransient && retries > 0) {
            const delay = (4 - retries) * 2000 // 2s, 4s, 6s backoff
            console.warn(`User profile fetch failed (${msg}), retrying in ${delay / 1000}s... (${retries} left)`)
            await new Promise(r => setTimeout(r, delay))
            return fetchUserData(userId, retries - 1)
          }
          // No profile row for this session. Either the account was deleted
          // out from under a live token, or handle_new_user never ran. Retry
          // briefly in case we beat the trigger, then drop the zombie session
          // instead of leaving the app authenticated with no user — that state
          // renders empty pages and can't be recovered by refreshing.
          const isMissingRow = userError.code === 'PGRST116'
            || msg.includes('Cannot coerce the result to a single JSON object')
          if (isMissingRow) {
            if (retries > 0) {
              await new Promise(r => setTimeout(r, 1000))
              return fetchUserData(userId, retries - 1)
            }
            console.warn('No profile row for the signed-in user — clearing session')
            await clearZombieSession()
            return
          }
          console.warn('User profile not found:', userError.message)
          return
        }

        if (userData && mounted) {
          // Extract organization and user data
          const userRecord = userData as Record<string, unknown>
          const orgsData = userRecord.organizations as Organization | null

          // Create user object without organizations property
          const userWithoutOrgs = {
            id: userRecord.id as string,
            created_at: userRecord.created_at as string,
            updated_at: userRecord.updated_at as string,
            email: userRecord.email as string,
            full_name: userRecord.full_name as string,
            avatar_url: userRecord.avatar_url as string | null,
            organization_id: userRecord.organization_id as string,
            role: userRecord.role as User['role'],
            vendor_id: (userRecord.vendor_id as string | null) ?? null,
            client_id: (userRecord.client_id as string | null) ?? null,
            portal_type: (userRecord.portal_type as string) ?? 'internal',
            page_permissions: (userRecord.page_permissions as string[]) ?? ['*'],
            column_permissions: (userRecord.column_permissions as Record<string, string[]>) ?? {},
            status: (userRecord.status as string) ?? 'active',
            is_platform_admin: (userRecord.is_platform_admin as boolean) ?? false,
            is_super_admin: (userRecord.is_super_admin as boolean) ?? false,
            must_change_password: (userRecord.must_change_password as boolean) ?? false,
            can_manage_parts: (userRecord.can_manage_parts as boolean) ?? false,
            can_view_financials: (userRecord.can_view_financials as boolean) ?? false,
            can_review_receipts: (userRecord.can_review_receipts as boolean) ?? false,
          } as unknown as User

          setUser(userWithoutOrgs)
          setOrganization(orgsData)
          saveProfileCache(userId, userWithoutOrgs, orgsData)
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
        const cached = loadProfileCache(userId)
        if (cached && mounted) {
          setUser(cached.user)
          setOrganization(cached.organization)
        }
      }
    }

    initAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        setSession(session)
        setSupabaseUser(session?.user ?? null)

        if (event === 'PASSWORD_RECOVERY') {
          // A "set password" / recovery link (e.g. an invited Monday-synced
          // member) established a session. Route them to the set-password page
          // regardless of where the link's redirect landed — the recovery
          // session is already persisted, so a same-origin nav keeps it.
          if (session?.user) await fetchUserData(session.user.id)
          if (!window.location.pathname.startsWith('/change-password')) {
            window.location.assign('/change-password')
          }
        } else if (event === 'SIGNED_IN' && session?.user) {
          await fetchUserData(session.user.id)
        } else if (event === 'SIGNED_OUT') {
          reset()
          setIsLoading(false)
          setIsInitialized(true)
        } else if (event === 'TOKEN_REFRESHED') {
          // Scope to active observers only — see comment in refreshIfNeeded.
          queryClient.invalidateQueries({ refetchType: 'active' })
        }
      }
    )

    // Proactively refresh session on tab focus, visibility change, and on an
    // interval. Browsers throttle timers in background/idle tabs, so Supabase's
    // autoRefreshToken often doesn't fire. Without this, the first action after
    // a long idle fires queries with an expired JWT that hangs or 401s.
    let isRefreshing = false
    let lastRefreshAt = Date.now()
    const refreshIfNeeded = async (reason: string) => {
      if (!mounted || isRefreshing) return
      isRefreshing = true
      try {
        // refreshSessionSafe wraps refreshSession() with a 15s hard timeout.
        // Without that, a stalled refresh hangs forever (the auth endpoint
        // was previously timeout-exempt in the supabase client), which
        // latched isRefreshing=true and froze every subsequent refresh
        // attempt — the root cause of "pages don't load after waiting".
        const { data, error } = await refreshSessionSafe()
        if (error) {
          console.warn(`Session refresh (${reason}) failed:`, error.message)
          // Last-resort self-heal: a refresh that *timed out* (not a real auth
          // error like an invalid refresh token) means the supabase-js client
          // is wedged on a dead internal promise — no amount of retrying in this
          // tab will recover it. Reload once to rebuild the client + reconnect
          // realtime so kiosk/long-idle pages come back on their own without a
          // manual refresh. Only when the tab is foreground, and throttled so it
          // can never loop. Real auth errors fall through to normal handling.
          //
          // But NEVER reload out from under someone who is mid-edit — a reload
          // there wipes an in-progress form (e.g. the Add to-do dialog) and the
          // user has to start over. If they're actively editing, the page is
          // clearly in use (not a dead kiosk), so defer: the next idle refresh
          // cycle will heal it once the form is closed.
          if (
            error.message.includes('timed out') &&
            document.visibilityState === 'visible' &&
            !shouldDeferReload()
          ) {
            try {
              const KEY = 'cs-auth-recover-reload-at'
              const last = Number(sessionStorage.getItem(KEY) || '0')
              if (Date.now() - last > 20_000) {
                sessionStorage.setItem(KEY, String(Date.now()))
                window.location.reload()
              }
            } catch {
              window.location.reload()
            }
          }
          return
        }
        if (data.session && mounted) {
          lastRefreshAt = Date.now()
          setSession(data.session)
          setSupabaseUser(data.session.user ?? null)
          // Invalidate active queries only so the refetch storm is bounded
          // to what's actually rendered. Pages with 20+ background queries
          // (WO detail, etc.) used to lock up the UI on every refresh.
          queryClient.invalidateQueries({ refetchType: 'active' })
        }
      } catch (err) {
        console.warn(`Session refresh (${reason}) threw:`, err)
      } finally {
        isRefreshing = false
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded('visibility')
      }
    }
    const handleFocus = () => refreshIfNeeded('focus')

    // Safety net: refresh every 3 minutes while tab is open. JWT expires at 1hr
    // default; this keeps it well ahead of expiry even if timers get throttled
    // and gives a 15-20 min idle window before the user hits a stale token.
    const refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded('interval')
      }
    }, 3 * 60 * 1000)

    // Activity-driven refresh: if user interacts after >90s of stillness,
    // pre-flight a session refresh so the upcoming click/upload/save doesn't
    // race a stale token. Throttled to once every 90s to avoid hammering auth.
    const ACTIVITY_REFRESH_THRESHOLD_MS = 90 * 1000
    const handleActivity = () => {
      if (!mounted) return
      if (Date.now() - lastRefreshAt < ACTIVITY_REFRESH_THRESHOLD_MS) return
      refreshIfNeeded('activity')
    }
    const activityEvents: Array<keyof DocumentEventMap> = ['mousedown', 'keydown', 'touchstart']
    activityEvents.forEach((ev) => document.addEventListener(ev, handleActivity, { passive: true }))

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      mounted = false
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      activityEvents.forEach((ev) => document.removeEventListener(ev, handleActivity))
      clearInterval(refreshInterval)
    }
  }, [setSession, setSupabaseUser, setUser, setOrganization, setIsLoading, setIsInitialized, reset, queryClient])

  return <>{children}</>
}
